import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  PageContainer, DataTable, PremiumCard, PremiumModal, Button,
  Field, SelectField, EmptyState,
} from "components/ui";

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconUpload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const CategoriesList = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const tenant = user?.tenant;
  const fileInputRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [form, setForm] = useState({ name: "", taxId: "" });
  const [taxes, setTaxes] = useState([]);

  const fetchCategories = async () => {
    try {
      const res = await axiosPrivate.get("/api/categories");
      setCategories(res.data);
    } catch (_) {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };
  const fetchTaxes = async () => {
    try {
      const res = await axiosPrivate.get("/api/taxes");
      setTaxes(res.data);
    } catch (err) {
      console.error("Failed to load taxes", err);
    }
  };

  useEffect(() => {
    if (tenant) { fetchCategories(); fetchTaxes(); }
    else setLoading(false);
  }, [tenant]);

  const openModal = (cat = null) => {
    if (cat) { setEditingCat(cat); setForm({ name: cat.name, taxId: cat.taxId || "" }); }
    else     { setEditingCat(null); setForm({ name: "", taxId: "" }); }
    setModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingCat) {
        await axiosPrivate.put(`/api/categories/${editingCat.id}`, form);
        toast.success("Category updated");
      } else {
        await axiosPrivate.post("/api/categories", form);
        toast.success("Category created");
      }
      setModal(false);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving category");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await axiosPrivate.delete(`/api/categories/${id}`);
      toast.success("Category deleted");
      fetchCategories();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axiosPrivate.post("/api/categories/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const imported = response.data;
      if (imported && imported.length > 0) {
        toast.success(`${imported.length} categories imported successfully!`);
        fetchCategories();
      } else {
        toast.warn("No categories were imported. Check your file headers.");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data || "Import failed.";
      toast.error(msg);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (!tenant) {
    return (
      <PageContainer>
        <PremiumCard>
          <EmptyState
            title="No organization yet"
            description="Create your organization first to manage categories."
            action={<Button onClick={() => navigate("/tenant/add-tenant")}>Create organization</Button>}
          />
        </PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <DataTable
        title="Categories"
        subtitle="Product categories and tax assignments"
        toolbar={
          <>
            <Button variant="secondary" size="sm" leftIcon={<IconUpload />} onClick={() => fileInputRef.current?.click()}>
              Import Excel
            </Button>
            <input
              type="file" accept=".xlsx,.xls" ref={fileInputRef}
              style={{ display: "none" }} onChange={handleFileChange}
            />
            <Button variant="primary" size="sm" leftIcon={<IconPlus />} onClick={() => openModal()}>
              Add category
            </Button>
          </>
        }
        loading={loading}
        data={categories}
        rowKey={(c) => c.id}
        searchPlaceholder="Search categories…"
        pageSize={10}
        emptyTitle="No categories yet"
        emptyAction={<Button onClick={() => openModal()} leftIcon={<IconPlus />}>Add category</Button>}
        columns={[
          {
            key: "name", header: "Name",
            render: (c) => (
              <button
                type="button"
                onClick={() => navigate(`/tenant/categories/${c.id}`)}
                style={{ background: "transparent", border: 0, color: "var(--brand-primary)", fontWeight: 600, cursor: "pointer", padding: 0 }}
              >
                {c.name}
              </button>
            ),
          },
          { key: "taxName", header: "Tax", render: (c) => c.taxName || "—" },
          {
            key: "actions", header: "", align: "right",
            render: (c) => (
              <div style={{ display: "inline-flex", gap: 6 }}>
                <Button variant="secondary" size="sm" onClick={() => openModal(c)}>Edit</Button>
                <Button variant="danger" size="sm" onClick={() => handleDelete(c.id)}>Delete</Button>
              </div>
            ),
          },
        ]}
      />

      <PremiumModal
        isOpen={modal}
        toggle={() => setModal(false)}
        title={editingCat ? "Edit category" : "New category"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSave}>Save</Button>
          </>
        }
      >
        <div style={{ display: "grid", gap: 16 }}>
          <Field
            label="Name" required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Beverages"
          />
          <SelectField
            label="Tax"
            value={form.taxId}
            onChange={(e) => setForm({ ...form, taxId: e.target.value })}
          >
            <option value="">— None —</option>
            {taxes.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.rate}%)</option>
            ))}
          </SelectField>
        </div>
      </PremiumModal>
    </PageContainer>
  );
};

export default CategoriesList;
